import { useState, type ReactNode } from 'react'
import clsx from 'clsx'
import { ChevronDown } from '@/assets/icons'
import { CaseCountBadge } from '@/citator/components/CaseCountBadge'

export type CitatorAccordionProps = {
  title: string
  count?: number
  children: ReactNode
  defaultOpen?: boolean
  open?: boolean
  /** Header disclosure state for accordions whose body stays mounted while content expands. */
  chevronExpanded?: boolean
  onHeaderClick?: () => void
  onOpenChange?: (open: boolean) => void
  fillHeight?: boolean
}

export function CitatorAccordion({
  title,
  count,
  children,
  defaultOpen = false,
  open,
  chevronExpanded,
  onHeaderClick,
  onOpenChange,
  fillHeight = false,
}: CitatorAccordionProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen)
  const isOpen = open ?? uncontrolledOpen
  const isHeaderExpanded = chevronExpanded ?? isOpen

  const toggle = () => {
    if (onHeaderClick) {
      onHeaderClick()
      return
    }

    const next = !isOpen
    if (open === undefined) {
      setUncontrolledOpen(next)
    }
    onOpenChange?.(next)
  }

  return (
    <div className={clsx('flex flex-col border border-true-gray-200 rounded bg-white', fillHeight && isOpen ? 'flex-1 min-h-[240px]' : 'shrink-0')}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={isHeaderExpanded}
        className={clsx(
          'w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-brand-neutral-50 transition-colors',
          // Square bottom corners when open so the body sits flush against the divider.
          isOpen ? 'rounded-t' : 'rounded'
        )}
      >
        <span className="flex items-center gap-2 font-rethink-sans font-semibold text-base leading-7 text-brand-neutral-950">
          {title}
          {count !== undefined && <CaseCountBadge count={count} />}
        </span>
        <span className="inline-flex items-center justify-center p-0.5 rounded-full bg-brand-neutral-200 border border-brand-neutral-300">
          <ChevronDown className={clsx('w-4 h-4 text-brand-neutral-700 transition-transform', isHeaderExpanded ? 'rotate-180' : 'rotate-0')} />
        </span>
      </button>
      {isOpen && (
        <div className={clsx('flex flex-col px-3 py-4 border-t border-true-gray-200', fillHeight && 'flex-1 min-h-0', !fillHeight && 'shrink-0')}>
          {children}
        </div>
      )}
    </div>
  )
}
